import { LeagueConfig } from '@leagueplatform/core';
import { usePatientPortalContext } from 'contexts/patient-portal-provider.context';
import {
  PATIENT_PORTAL_SYSTEM_IDS,
  PatientPortalSystemOptions,
} from 'types/patient-portal-system.types';

// Theme imports
import { medibankAmplarHealthTheme } from 'theme/amplar/medibank-amplar.theme';
import { medibankMyHealthTheme } from 'theme/my-health/medibank-myhealth.theme';

// Icon imports
import { GDSColor } from '@leagueplatform/genesis-core';
import HomeNavIcon from '../assets/amplar/icons/homeNavIcon.svg';
import HomeNavIconFilled from '../assets/amplar/icons/homeNavIconFilled.svg';
import DocumentsNavIcon from '../assets/amplar/icons/documentsNavIcon.svg';
import DocumentsNavIconFilled from '../assets/amplar/icons/documentsNavIconFilled.svg';
import AppointmentsNavIcon from '../assets/amplar/icons/appointmentsNavIcon.svg';
import AppointmentsNavIconFilled from '../assets/amplar/icons/appointmentsNavIconFilled.svg';
import JourneyNavIcon from '../assets/amplar/icons/journeyNavIcon.svg';
import JourneyNavIconFilled from '../assets/amplar/icons/journeyNavIconFilled.svg';

// Landing Background imports
import AmplarLandingBackground from '../assets/amplar/pre-login-background.png';
import MyHealthLandingBackground from '../assets/my-health/pre-login-background.png';

// Logo imports
import AmplarLogo from '../assets/amplar/AmplarLogo.svg';
import MyHealthLogo from '../assets/my-health/MyHealthLogo.svg';

// Feature Highlights Background imports
import AmplarFeatureHighlightsBackground from '../assets/amplar/feature-highlights-background-amplar.png';
import MyHealthFeatureHighlightsBackground from '../assets/my-health/feature-highlights-background-myhealth.png';

// Journey History empty state
import AmplarActivityHistoryTrophy from '../assets/amplar/activity-history-trophy.png';
import MyHealthActivityHistoryTrophy from '../assets/my-health/activity-history-trophy.png';

// Documents Icon overrides
import InterfaceVisibleOverride from '../assets/interfaceVisibleOverride.svg';
import InterfaceDownloadOverride from '../assets/interfaceDownloadOverride.svg';
import InterfaceRefreshOverride from '../assets/revision-icon.svg';

interface PateintPortalUIConfigOptionsProps {
  coreUI: LeagueConfig['core']['ui'];
  landing: { backgroundImageSrc: string };
  logo: string;
  assets?: LeagueConfig['assets'];
  footer: { backgroundColor: GDSColor };
  errorState: { backgroundColor: GDSColor };
}

export const PatientPortalUIConfigOptions: {
  [k in PatientPortalSystemOptions]: PateintPortalUIConfigOptionsProps;
} = {
  [PATIENT_PORTAL_SYSTEM_IDS.AMPLAR]: {
    coreUI: {
      theme: medibankAmplarHealthTheme,
      themeOptions: {
        customIcons: {
          homeNavIcon: HomeNavIcon,
          homeNavIconFilled: HomeNavIconFilled,
          documentsNavIcon: DocumentsNavIcon,
          documentsNavIconFilled: DocumentsNavIconFilled,
          appointmentsNavIcon: AppointmentsNavIcon,
          appointmentsNavIconFilled: AppointmentsNavIconFilled,
          journeyNavIcon: JourneyNavIcon,
          journeyNavIconFilled: JourneyNavIconFilled,
          interfaceVisible: InterfaceVisibleOverride,
          interfaceDownload: InterfaceDownloadOverride,
          interfaceRefresh: InterfaceRefreshOverride,
        },
      },
      components: {
        pageHeader: {
          shape: 'line',
          backgroundColor: 'decorative.brand.primary.pastel',
        },
      },
    },
    landing: { backgroundImageSrc: AmplarLandingBackground },
    logo: AmplarLogo,
    assets: {
      overrides: {
        FEATURE_HIGHLIGHT_BACKGROUND: AmplarFeatureHighlightsBackground,
        EMPTY_STATE_HISTORY: AmplarActivityHistoryTrophy,
      },
    },
    footer: { backgroundColor: '$surfaceBackgroundTertiary' },
    errorState: { backgroundColor: '$surfaceBackgroundPrimary' },
  },
  [PATIENT_PORTAL_SYSTEM_IDS.MY_HEALTH]: {
    coreUI: {
      theme: medibankMyHealthTheme,
      themeOptions: {
        customIcons: {
          homeNavIcon: HomeNavIcon,
          homeNavIconFilled: HomeNavIconFilled,
          documentsNavIcon: DocumentsNavIcon,
          documentsNavIconFilled: DocumentsNavIconFilled,
          appointmentsNavIcon: AppointmentsNavIcon,
          appointmentsNavIconFilled: AppointmentsNavIconFilled,
          journeyNavIcon: JourneyNavIcon,
          journeyNavIconFilled: JourneyNavIconFilled,
          interfaceVisible: InterfaceVisibleOverride,
          interfaceDownload: InterfaceDownloadOverride,
          interfaceRefresh: InterfaceRefreshOverride,
        },
      },
      components: {
        pageHeader: {
          shape: 'line',
          backgroundColor: 'decorative.brand.primary.pastel',
        },
      },
    },
    landing: { backgroundImageSrc: MyHealthLandingBackground },
    logo: MyHealthLogo,
    assets: {
      overrides: {
        FEATURE_HIGHLIGHT_BACKGROUND: MyHealthFeatureHighlightsBackground,
        EMPTY_STATE_HISTORY: MyHealthActivityHistoryTrophy,
      },
    },
    footer: { backgroundColor: '$surfaceBackgroundSecondary' },
    errorState: { backgroundColor: '$surfaceBackgroundSecondary' },
  },
};

export const usePatientPortalUIConfigOptions = () => {
  const { systemId } = usePatientPortalContext();
  return PatientPortalUIConfigOptions[systemId];
};
